import { useState, useEffect } from 'react'
import { collection, doc, getDoc, addDoc, setDoc, query, where, updateDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'

/**
 * Manages access control for a specific family
 */
export function useAccessControl(user, familyId) {
  const [isApproved, setIsApproved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [accessRequest, setAccessRequest] = useState(null)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    if (!familyId) {
      setIsApproved(false)
      setLoading(false)
      return
    }

    // Listen to familyMembers doc for live approval updates
    const memberRef = doc(db, 'familyMembers', familyId)
    const unsubMember = onSnapshot(memberRef, (memberDoc) => {
      if (memberDoc.exists()) {
        const members = memberDoc.data().members || {}
        if (members[user.uid]) {
          setIsApproved(true)
          setLoading(false)
          return
        }
      }
      setIsApproved(false)
      setLoading(false)
    }, (err) => {
      console.error('Error listening to member status:', err)
      setLoading(false)
    })

    // Listen to access request for this user+family
    const requestsRef = collection(db, 'accessRequests')
    const q = query(requestsRef, where('userId', '==', user.uid), where('familyId', '==', familyId))
    const unsubRequests = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setAccessRequest({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() })
      } else {
        setAccessRequest(null)
      }
    }, (err) => {
      console.error('Error listening to access requests:', err)
    })

    return () => {
      unsubMember()
      unsubRequests()
    }
  }, [user?.uid, familyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const submitAccessRequest = async (note) => {
    if (!user || !familyId) return

    try {
      const requestData = {
        userId: user.uid,
        email: user.email,
        familyId: familyId,
        note: note || '',
        status: 'pending',
        requestedAt: new Date().toISOString()
      }

      if (accessRequest) {
        // Update existing request
        await setDoc(doc(db, 'accessRequests', accessRequest.id), requestData, { merge: true })
      } else {
        // Create new request
        await addDoc(collection(db, 'accessRequests'), requestData)
      }
      // onSnapshot will update accessRequest state automatically

      return true
    } catch (err) {
      console.error('Error submitting access request:', err)
      throw err
    }
  }

  return {
    isApproved,
    loading,
    accessRequest,
    submitAccessRequest
  }
}

/**
 * Manages access requests for a specific family (admin view)
 */
export function useAccessRequests(user, familyId) {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user || !familyId) {
      setLoading(false)
      return
    }

    const requestsRef = collection(db, 'accessRequests')
    const q = query(requestsRef, where('familyId', '==', familyId), where('status', '==', 'pending'))

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setRequests(data)
      setLoading(false)
    }, (err) => {
      console.error('Error listening to access requests:', err)
      setLoading(false)
    })

    return () => unsub()
  }, [user?.uid, familyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const approveRequest = async (requestId, requestUserId, requestUserEmail) => {
    if (!user || !familyId) return

    try {
      // Add user to family members
      const memberRef = doc(db, 'familyMembers', familyId)
      const memberDoc = await getDoc(memberRef)

      const members = memberDoc.exists() ? (memberDoc.data().members || {}) : {}
      members[requestUserId] = {
        email: requestUserEmail,
        role: 'member',
        approvedBy: user.uid,
        joinedAt: new Date().toISOString()
      }

      await setDoc(memberRef, { members }, { merge: true })

      // Update family member count
      const familyRef = doc(db, 'families', familyId)
      await updateDoc(familyRef, {
        memberCount: Object.keys(members).length
      })

      // Update request status
      await setDoc(doc(db, 'accessRequests', requestId), {
        status: 'approved',
        reviewedBy: user.uid,
        reviewedAt: new Date().toISOString()
      }, { merge: true })

      // onSnapshot will remove it from the pending list automatically
      return true
    } catch (err) {
      console.error('Error approving request:', err)
      throw err
    }
  }

  const rejectRequest = async (requestId) => {
    try {
      await setDoc(doc(db, 'accessRequests', requestId), {
        status: 'rejected',
        reviewedBy: user.uid,
        reviewedAt: new Date().toISOString()
      }, { merge: true })

      // onSnapshot will remove it from the pending list automatically
      return true
    } catch (err) {
      console.error('Error rejecting request:', err)
      throw err
    }
  }

  return {
    requests,
    loading,
    approveRequest,
    rejectRequest
  }
}
